import { Controller } from '@hotwired/stimulus'

// Controls visibility of trigger configuration fields based on selected trigger type
export default class extends Controller {
  static targets = ['statusConfig', 'fieldConfig', 'dateOnConfig', 'dateConfig', 'dateDirection', 'dateFieldBefore', 'dateFieldAfter']

  // Mapping of trigger types to default email templates
  static TRIGGER_TEMPLATE_MAP = {
    status_changed: 'task_status_changed',
    field_changed: 'task_assigned', // assignee_changed
    created: 'task_assigned',
    completed: 'task_status_changed',
    date_on: 'task_due_soon',
    date_before: 'task_due_soon',
    date_after: 'task_overdue'
  }

  connect() {
    this.updateVisibility()
    // Auto-select template on initial load, but only for new automations
    if (this.isNewAutomation()) {
      const select = this.element.querySelector('select[name*="event_type"]')
      if (select) {
        this.autoSelectTemplate(select.value)
      }
    }
  }

  isNewAutomation() {
    // Check if this is an existing automation by looking for a persisted ID field
    const form = this.element.closest('form')
    if (!form) return true

    // Look for hidden automation ID field with a value
    const idField = form.querySelector('input[type="hidden"][name$="[id]"]')
    return !idField || !idField.value
  }

  typeChanged(event) {
    this.updateVisibility()
    // Only auto-select template for new automations
    if (this.isNewAutomation()) {
      this.autoSelectTemplate(event.target.value)
    }
    // Dispatch event so action configs can update their visibility
    this.dispatchTriggerTypeChanged(event.target.value)
  }

  dispatchTriggerTypeChanged(triggerType) {
    const isDateBased = triggerType?.startsWith('date_')
    // Dispatch to the form so all action configs can listen
    const form = this.element.closest('form')
    if (form) {
      form.dispatchEvent(new CustomEvent('automation:triggerTypeChanged', {
        bubbles: true,
        detail: { triggerType, isDateBased }
      }))
    }
  }

  updateVisibility() {
    const select = this.element.querySelector('select[name*="event_type"]')
    if (!select) return

    const triggerType = select.value

    // Hide and disable all config sections first
    this.hideAll()

    // Show relevant config based on trigger type
    switch (triggerType) {
      case 'status_changed':
        this.showTarget('statusConfig')
        break
      case 'field_changed':
        this.showTarget('fieldConfig')
        break
      case 'date_on':
        this.showTarget('dateOnConfig')
        break
      case 'date_before':
        this.showTarget('dateConfig')
        this.updateDateDirection('before')
        this.showDateField('before')
        break
      case 'date_after':
        this.showTarget('dateConfig')
        this.updateDateDirection('after')
        this.showDateField('after')
        break
    }
  }

  updateDateDirection(direction) {
    if (this.hasDateDirectionTarget) {
      this.dateDirectionTarget.textContent = direction
    }
  }

  showDateField(type) {
    if (this.hasDateFieldBeforeTarget) {
      this.dateFieldBeforeTarget.classList.toggle('d-none', type !== 'before')
      this.setInputsDisabled(this.dateFieldBeforeTarget, type !== 'before')
    }
    if (this.hasDateFieldAfterTarget) {
      this.dateFieldAfterTarget.classList.toggle('d-none', type !== 'after')
      this.setInputsDisabled(this.dateFieldAfterTarget, type !== 'after')
    }
  }

  hideAll() {
    if (this.hasStatusConfigTarget) {
      this.statusConfigTarget.classList.add('d-none')
      this.setInputsDisabled(this.statusConfigTarget, true)
    }
    if (this.hasFieldConfigTarget) {
      this.fieldConfigTarget.classList.add('d-none')
      this.setInputsDisabled(this.fieldConfigTarget, true)
    }
    if (this.hasDateOnConfigTarget) {
      this.dateOnConfigTarget.classList.add('d-none')
      this.setInputsDisabled(this.dateOnConfigTarget, true)
    }
    if (this.hasDateConfigTarget) {
      this.dateConfigTarget.classList.add('d-none')
      this.setInputsDisabled(this.dateConfigTarget, true)
    }
  }

  showTarget(targetName) {
    const target = this[`${targetName}Target`]
    if (target) {
      target.classList.remove('d-none')
      this.setInputsDisabled(target, false)
    }
  }

  // Disable/enable all inputs within a container to prevent hidden fields from being submitted
  setInputsDisabled(container, disabled) {
    if (!container) return
    container.querySelectorAll('input, select, textarea').forEach(input => {
      input.disabled = disabled
    })
  }

  // Auto-select email template based on trigger type
  autoSelectTemplate(triggerType) {
    const defaultTemplate = this.constructor.TRIGGER_TEMPLATE_MAP[triggerType]
    if (!defaultTemplate) return

    // Find the parent form that contains both trigger and actions
    // The form wraps both WHEN (trigger) and THEN (actions) sections
    const form = this.element.closest('form')
    if (!form) return

    // Find template selects within the form
    const templateSelects = form.querySelectorAll('select[name*="[template]"]')

    templateSelects.forEach(select => {
      // Skip if already set to the correct template
      if (select.value === defaultTemplate) return

      const option = select.querySelector(`option[value="${defaultTemplate}"]`)
      if (option) {
        select.value = defaultTemplate
        // Trigger change event so the preview updates
        select.dispatchEvent(new Event('change', { bubbles: true }))
      }
    })
  }
}
